import type { TimebankStatus } from "@/hooks/use-timebank";

/**
 * Etiketter og merkelapp-stiler for timebank-statuser.
 *
 * Lå tidligere inline i BÅDE `TimebankCard.tsx` (ansattens eget kort) og
 * `AllTransactionsAdminTable.tsx` (admin-tabellen), og de to hadde alt begynt
 * å drifte: `cancelled` het «Kansellert» ett sted og «Fjernet» det andre. Da
 * VIB-2766 skulle legge til en tredje flate ble det tydelig at kopi nummer tre
 * var feil svar.
 *
 * Valgt etikett for `cancelled` er **«Fjernet»**. «Kansellert» kan forveksles
 * med at den ansatte selv trakk søknaden, som er noe annet, og admin-flyten
 * som setter statusen sier allerede «Transaksjon fjernet» i kvitteringen.
 *
 * Fargene er workflow-status, ikke KPI-vurdering, så de hører ikke under
 * `widget-status.ts`-paletten. De er løftet hit uendret fra kortet for å
 * holde det ansatte ser likt det lederen ser.
 */
export const TIMEBANK_STATUS_LABEL = {
  pending: "Venter",
  approved: "Godkjent",
  rejected: "Avslått",
  cancelled: "Fjernet",
} as const satisfies Record<TimebankStatus, string>;

export const TIMEBANK_STATUS_STYLE = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-muted text-muted-foreground",
} as const satisfies Record<TimebankStatus, string>;

/**
 * Etikett for en status som kommer fra basen som fri tekst.
 *
 * `timebank_transactions.status` er en `text`-kolonne, ikke en enum, så en rad
 * skrevet utenom appen kan bære en verdi vi ikke kjenner. Da viser vi råverdien
 * framfor ingenting: en ukjent status er informasjon, en tom celle er ikke.
 */
export function timebankStatusLabel(status: string): string {
  return TIMEBANK_STATUS_LABEL[status as TimebankStatus] ?? status;
}

/** Stil for en status fra basen. Ukjent status får den nøytrale stilen. */
export function timebankStatusStyle(status: string): string {
  return TIMEBANK_STATUS_STYLE[status as TimebankStatus] ?? TIMEBANK_STATUS_STYLE.cancelled;
}
